import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { GridApiPro, GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { roundDecimalValue } from '../helper';
import Tooltip from '@mui/material/Tooltip';
import { ColumnTypeIdentifier, RowTypesIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { trimLeadingZeros } from 'components/editor/helpers/trimLeadingZeros';
import { useTranslation } from 'react-i18next';

const NON_NEGATIVE_COLUMN_TYPES = [ColumnTypeIdentifier.PRICE, ColumnTypeIdentifier.MULTIPLIER];

const isNegative = (value: string | number): boolean => String(value).trim().startsWith('-');

const isInvalid = (value: string | number, columnType: ColumnTypeIdentifier): boolean =>
  NON_NEGATIVE_COLUMN_TYPES.includes(columnType) && isNegative(value);

type InputWithTooltipsProps = Partial<GridRenderEditCellParams> & {
  blockId: string;
  numberOfDecimalPlaces?: number;
  tooltipTitle?: string | null;
  onInputChange?: (value: string) => void;
  onInputBlur?: (roundedValue: string) => void;
  onPressEnter?: (event: React.KeyboardEvent<HTMLInputElement>, apiRef: GridApiPro, localValue: string) => void;
  children?: React.ReactNode;
  reduxCellValue?: string;
  lastSentValueRef?: React.MutableRefObject<string | null>;
  currencySymbol?: string;
};

export const InputWithTooltips: React.FC<InputWithTooltipsProps> = ({
  params,
  blockId,
  numberOfDecimalPlaces,
  tooltipTitle = null,
  onInputChange,
  onInputBlur,
  onPressEnter,
  children,
  reduxCellValue,
  lastSentValueRef,
  currencySymbol,
}) => {
  const { t } = useTranslation();
  const { id, value, field, colDef, row } = params;
  const [localValue, setLocalValue] = useState<string>(value);
  const apiRef = useGridApiContext();
  const [formattedValue, setFormattedValue] = useState<{ value: string; blockId: string }>({ value: localValue, blockId: blockId });
  const isNumericInput = numberOfDecimalPlaces !== undefined && !isNaN(numberOfDecimalPlaces);
  const isNumberInput = colDef.type === 'number';
  const isNegativeValue = isInvalid(localValue, colDef.columnType as ColumnTypeIdentifier);

  const localValueRef = useRef<string>(value);
  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  const lastValidValueRef = useRef<string>(value);
  useEffect(() => {
    if (!isNegativeValue) {
      lastValidValueRef.current = localValue;
    }
  }, [localValue, isNegativeValue]);

  useEffect(() => {
    if (reduxCellValue === undefined) return;

    if (lastSentValueRef?.current === reduxCellValue) return;

    if (localValueRef.current === reduxCellValue) return;

    setLocalValue(reduxCellValue);
    setFormattedValue({ value: reduxCellValue, blockId });

    apiRef.current?.setEditCellValue({
      id,
      field,
      value: reduxCellValue,
    });
  }, [reduxCellValue]);

  const handleOnChange = async (value: string) => {
    if (!blockId) return;

    if (isNumericInput) {
      value = value.replace(/e/gi, '');
      const roundedValue = roundDecimalValue(value, numberOfDecimalPlaces);

      setFormattedValue({ value: roundedValue, blockId: blockId as string });
      setLocalValue(roundedValue);
    } else {
      setFormattedValue({ value: value, blockId: blockId as string });
    }

    setLocalValue(value);
    apiRef.current?.setEditCellValue({
      id,
      field,
      value: value,
    });

    if (isInvalid(value, colDef.columnType as ColumnTypeIdentifier)) return;

    onInputChange?.(value);
  };

  const handleBlur = async () => {
    if (isNegativeValue) {
      const revertedValue = lastValidValueRef.current;
      setLocalValue(revertedValue);
      setFormattedValue({ value: revertedValue, blockId: blockId as string });
      apiRef.current?.setEditCellValue({
        id,
        field,
        value: revertedValue,
      });
      return;
    }

    let roundedValue = formattedValue?.value;
    const trimmedValue = isNumberInput ? trimLeadingZeros(roundedValue) : roundedValue;

    if (isNumericInput && !Number(roundedValue)) {
      roundedValue = '0';
    }

    setLocalValue(roundedValue);
    apiRef.current?.setEditCellValue({
      id,
      field,
      value: trimmedValue,
    });

    onInputChange?.(trimmedValue);
    onInputBlur?.(trimmedValue);
  };

  const handlePressEnter = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab') {
      await handleBlur();
    } else if (event.key === 'e' && numberOfDecimalPlaces) {
      event.stopPropagation();
      await handleBlur();
    }
    if (apiRef.current) {
      onPressEnter?.(event, apiRef.current, localValue);
    }
  };

  const inputClassName = isNumericInput ? 'py-number-input-no-arrow' : 'py-custom-input';
  const isNotFooterORHeader = row.rowType !== RowTypesIdentifier.FOOTER && row.rowType !== RowTypesIdentifier.HEADER;
  const columnTypePlaceholders: Partial<Record<ColumnTypeIdentifier, string>> = {
    [ColumnTypeIdentifier.PRICE]: `${currencySymbol}0`,
    [ColumnTypeIdentifier.MULTIPLIER]: '0',
    [ColumnTypeIdentifier.SUBTOTAL]: `${currencySymbol}0`,
  };
  const inputPlaceholder = columnTypePlaceholders[colDef.columnType as ColumnTypeIdentifier] ?? '';

  return (
    <Tooltip title={isNegativeValue ? t('pricing.discount.error_invalid') : tooltipTitle} placement="bottom">
      <span className="py-custom-cell-wrapper">
        <input
          className={`${inputClassName}${isNumberInput ? ' py-numeric-cell-value' : ''}${isNegativeValue ? ' py-input-error' : ''}`}
          type={`${isNumberInput && isNotFooterORHeader ? 'number' : 'string'}`}
          onChange={(e) => handleOnChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => handlePressEnter(e)}
          data-testid-row-type={row.rowType}
          data-testid-column-type={colDef.columnType}
          data-testid="input-with-tooltips"
          value={localValue}
          placeholder={inputPlaceholder}
          autoFocus
        />
        {children}
      </span>
    </Tooltip>
  );
};

export default InputWithTooltips;
